/**
 * @module core/motion
 *
 * Motion preferences and capability detection.
 *
 * Accessibility and performance both depend on *not* animating in certain
 * situations. Everything that decides "should we animate this?" funnels through
 * here so the answer is consistent everywhere.
 */

/**
 * @typedef {Object} MotionPreferences
 * @property {boolean} reducedMotion  User asked for reduced motion.
 * @property {boolean} finePointer    Device has a precise pointer (mouse).
 * @property {boolean} hover          Device supports hover interactions.
 * @property {boolean} saveData       User/browser signalled data saving.
 * @property {boolean} lowMemory      Device reports a low memory class.
 */

/**
 * Read the current environment once. Values that can change at runtime (like
 * `reducedMotion`) are re-read through {@link onMotionPreferenceChange}.
 *
 * @returns {MotionPreferences}
 */
export function getMotionPreferences() {
  const hasWindow = typeof window !== "undefined";
  const match = (query) => (hasWindow && window.matchMedia ? window.matchMedia(query).matches : false);

  return {
    reducedMotion: match("(prefers-reduced-motion: reduce)"),
    finePointer: match("(pointer: fine)"),
    hover: match("(hover: hover)"),
    saveData: Boolean(/** @type {any} */ (hasWindow ? navigator : {}).connection?.saveData),
    lowMemory:
      typeof navigator !== "undefined" && "deviceMemory" in navigator
        ? Number(/** @type {any} */ (navigator).deviceMemory) <= 4
        : false,
  };
}

/**
 * Subscribe to changes of the reduced-motion preference.
 *
 * @param {(reduced: boolean) => void} callback
 * @returns {() => void} Disposer.
 */
export function onMotionPreferenceChange(callback) {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const query = window.matchMedia("(prefers-reduced-motion: reduce)");
  const handler = (event) => callback(event.matches);
  query.addEventListener("change", handler);
  return () => query.removeEventListener("change", handler);
}

/**
 * A conservative "is this a low-powered device?" heuristic. Used to decide
 * whether to enable expensive decoration such as particle swarms and smooth
 * scrolling.
 *
 * @returns {boolean}
 */
export function isLowPowerDevice() {
  const prefs = getMotionPreferences();
  return prefs.reducedMotion || prefs.saveData || prefs.lowMemory;
}

/**
 * True when the browser exposes the APIs we rely on for the enhanced
 * experience. When false the site still renders — it simply stays static.
 *
 * @returns {boolean}
 */
export function supportsEnhancedMotion() {
  if (typeof window === "undefined") return false;
  return Boolean(
    window.requestAnimationFrame &&
      window.CSS &&
      typeof window.CSS.supports === "function" &&
      CSS.supports("transform", "translate3d(0,0,0)")
  );
}
